// Cognito User Pool auth wrapper using AWS Amplify JS v6 (auth category only).
//
// This app is admin-only: the Cognito User Pool has `selfSignUpEnabled: false`,
// so the `SignUp` and `ConfirmSignUp` APIs are not exposed. New users are
// created by an admin via `aws cognito-idp admin-create-user`, which issues
// a one-time temporary password the user must change on first sign-in.
//
// Only the User Pool is configured here: the Identity Pool credential exchange
// stays in core/aws.js (it needs a fresh ID token per refresh — getFreshIdToken).
// Sign-in uses SRP (Amplify's default), matching the app client's `userSrp` flow.

import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  confirmSignIn,
  signOut as amplifySignOut,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { config } from '../core/config.js';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.userPoolId,
      userPoolClientId: config.userPoolClientId,
    },
  },
});

/**
 * Sign in. If the user is in the `FORCE_CHANGE_PASSWORD` state (issued a
 * temporary password by an admin), `newPassword` is required to complete the
 * new-password challenge.
 *
 * Resolves with `{ status: 'signedIn' | 'newPasswordRequired' }`.
 */
export async function signIn({ email, password, newPassword }) {
  let result;
  try {
    result = await amplifySignIn({ username: email, password });
  } catch (err) {
    // A stale local session (e.g. tokens that can no longer refresh) makes
    // Amplify refuse a new sign-in; clear it and try once more.
    if (err?.name !== 'UserAlreadyAuthenticatedException') throw err;
    await amplifySignOut();
    result = await amplifySignIn({ username: email, password });
  }

  if (result.isSignedIn) return { status: 'signedIn' };
  const step = result.nextStep?.signInStep;
  if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    // Signal to the UI to prompt for a new password and retry.
    if (!newPassword) return { status: 'newPasswordRequired' };
    const confirmed = await confirmSignIn({ challengeResponse: newPassword });
    if (confirmed.isSignedIn) return { status: 'signedIn' };
    throw unsupportedStep(confirmed.nextStep?.signInStep);
  }
  throw unsupportedStep(step);
}

function unsupportedStep(step) {
  const err = new Error(`Sign-in needs an unsupported step (${step ?? 'unknown'}).`);
  err.name = 'UnsupportedSignInStep';
  return err;
}

/** Sign out locally (clears the stored tokens). */
export async function signOut() {
  await amplifySignOut();
}

/**
 * Start the forgot-password flow: Cognito emails a verification code to the
 * user's (verified) email. Resolves once the code has been sent.
 *
 * Works for users in the normal CONFIRMED state and recovers users left in
 * RESET_REQUIRED by an admin "Reset password" action.
 */
export async function requestPasswordReset(email) {
  await resetPassword({ username: email });
}

/**
 * Complete the forgot-password flow with the emailed code and a new password.
 */
export async function confirmPasswordReset({ email, code, newPassword }) {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

/**
 * Resolve the currently signed-in user's session as `{ idToken, email }`, or
 * null if none. Amplify refreshes expired tokens via the refresh token.
 */
export async function getCurrentSession() {
  try {
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken;
    if (!idToken) return null;
    return { idToken: idToken.toString(), email: idToken.payload?.email ?? '' };
  } catch {
    return null;
  }
}

/**
 * Resolve a currently-valid User Pool ID token (JWT), refreshing it via the
 * refresh token if the cached one has expired. Returns null if there is no
 * valid session (the caller should redirect to sign-in).
 *
 * Use this anywhere a *fresh* token is needed on demand — e.g. the Cognito
 * Identity credential provider, which must not reuse a token captured at
 * sign-in (those expire after ~1h, causing "Token is inactive" errors).
 */
export async function getFreshIdToken() {
  const session = await getCurrentSession();
  return session ? session.idToken : null;
}

/**
 * Return the issuer string needed as the `Logins` key for the Cognito
 * Identity Pool when exchanging a User Pool token for IAM credentials.
 */
export function userPoolLoginsKey() {
  return `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
}
